import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

/**
 * Phase 27c · Iframe-safe embed player.
 * Route: /video/:id/embed
 * - No chrome, no navbar, full-viewport
 * - X-Frame-Options ALLOWALL emitted via meta tag (backend proxied)
 * - Same JSON-LD as /video/:id
 */
export default function VideoEmbed() {
  const { id } = useParams();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);
  const throttleRef = useRef({ start: 0, progress: 0, complete: 0 });
  const sessionIdRef = useRef(null);

  useEffect(() => {
    if (!sessionIdRef.current) {
      sessionIdRef.current = `embed-${Math.random().toString(36).slice(2, 12)}-${Date.now()}`;
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/videos/${encodeURIComponent(id)}`, {
        credentials: "include",
      });
      if (!r.ok) throw new Error(`http_${r.status}`);
      const data = await r.json();
      setVideo(data);
    } catch (e) {
      setError(e?.message || "load_error");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const beacon = useCallback((event_type, elapsed_sec = 0) => {
    const now = Date.now();
    if (throttleRef.current[event_type]
        && (now - throttleRef.current[event_type]) < 10000) return;
    throttleRef.current[event_type] = now;
    fetch(`${API}/api/videos/v2/${encodeURIComponent(id)}/watch-event`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_type, elapsed_sec,
        session_id: sessionIdRef.current,
      }),
    }).catch(() => {});
  }, [id]);

  // SEO — JSON-LD injection (identical schema to /video/:id)
  useEffect(() => {
    if (!video) return;
    try {
      const el = document.getElementById("video-embed-jsonld")
        || document.createElement("script");
      el.id = "video-embed-jsonld";
      el.type = "application/ld+json";
      const seoPub = video.seo_public_urls || {};
      el.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: video.title,
        description: video.description || (video.script || "").slice(0, 200),
        thumbnailUrl: seoPub.thumbnailUrl || `${window.location.origin}${API}/api/videos/thumbnail/${video.video_id}.svg`,
        uploadDate: video.created_at,
        duration: `PT${video.duration_sec || 45}S`,
        contentUrl: seoPub.contentUrl || `${window.location.origin}/video/${video.video_id}`,
        embedUrl: window.location.href,
      });
      if (!el.parentNode) document.head.appendChild(el);
      document.title = `${video.title} — Embed`;

      // OG meta tags for embed (Twitter/LinkedIn preview)
      const setMeta = (property, content) => {
        let m = document.querySelector(`meta[property="${property}"]`);
        if (!m) { m = document.createElement("meta"); m.setAttribute("property", property); document.head.appendChild(m); }
        m.setAttribute("content", content);
      };
      setMeta("og:type", "video.other");
      setMeta("og:title", video.title);
      setMeta("og:video", seoPub.contentUrl || "");
      setMeta("og:image", seoPub.thumbnailUrl || `${window.location.origin}${API}/api/videos/thumbnail/${video.video_id}.svg`);
    } catch {}
  }, [video]);

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-white" />
    </div>
  );

  if (error || !video) return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
      <p className="text-sm">Video not available.</p>
    </div>
  );

  const seoPub = video.seo_public_urls || {};
  const contentUrl = seoPub.contentUrl;
  const thumbUrl = seoPub.thumbnailUrl
    || `${API}/api/videos/thumbnail/${video.video_id}.svg`;
  const captionUrls = seoPub.captionUrls || {};

  return (
    <div className="min-h-screen bg-black flex items-center justify-center" data-testid="video-embed-page">
      {contentUrl ? (
        <video
          ref={videoRef}
          data-testid="video-embed-player"
          className="w-full h-screen"
          controls
          preload="metadata"
          poster={thumbUrl}
          onPlay={() => beacon("start", 0)}
          onTimeUpdate={() => beacon("progress", Math.floor(videoRef.current?.currentTime || 0))}
          onEnded={() => beacon("complete", Math.floor(videoRef.current?.duration || 0))}
          style={{ aspectRatio: "16/9", maxHeight: "100vh" }}
        >
          <source src={contentUrl} type="video/mp4" />
          {Object.entries(captionUrls).map(([lang, url]) => (
            <track key={lang} src={url} kind="subtitles" srcLang={lang} label={lang.toUpperCase()} />
          ))}
        </video>
      ) : (
        <div className="text-center text-white p-6">
          <img
            src={thumbUrl}
            alt={video.title}
            className="max-w-md mx-auto rounded-lg mb-4"
            data-testid="video-embed-thumbnail"
          />
          <h2 className="text-xl mb-2">{video.title}</h2>
          <p className="text-sm text-white/70">Video rendering non ancora completato.</p>
        </div>
      )}
    </div>
  );
}
